import { useAtom, useAtomValue, useSetAtom } from 'jotai'
import { atomWithReset, useResetAtom } from 'jotai/utils'
import { useCallback, useEffect, useRef } from 'react'
import { useMyInfo } from 'src/services/apollo/auth'
import {
  type BoostItem,
  useInvalidateBoosts,
  useMyAvailableBoosts,
  useRemoveBoostQueries,
  useResetBoostQueries,
} from 'src/services/orpc/extraPackageBoost'

export type { BoostItem as CouponItem }

// ---------- Coupon Dialog ----------
const couponDialogVisibleAtom = atomWithReset<boolean>(false)
const defaultCouponInitializedAtom = atomWithReset<boolean>(false)

export const useCouponDialogVisible = () => {
  return useAtomValue<boolean>(couponDialogVisibleAtom)
}

export const useOpenCouponListDialog = () => {
  const setter = useSetAtom(couponDialogVisibleAtom)
  return useCallback(() => setter(true), [setter])
}

export const useCloseCouponListDialog = () => {
  const setDialogVisible = useSetAtom(couponDialogVisibleAtom)

  return useCallback(() => {
    setDialogVisible(false)
  }, [setDialogVisible])
}
// ---------- Coupon Dialog ----------

// ---------- Applied Coupon ----------
const appliedCouponAtom = atomWithReset<string | null>(null)

export const useAppliedCoupons = () => {
  return useAtomValue<string | null>(appliedCouponAtom)
}

export function useEffectiveAppliedCoupon() {
  const manuallyApplied = useAtomValue(appliedCouponAtom)
  const { data } = useMyAvailableBoosts()

  if (manuallyApplied !== null) {
    return manuallyApplied
  }

  // Auto-apply the coupon with the highest boost rate
  if (!data || data.length === 0) return null
  const best = data.reduce((max, c) =>
    c.boostRatePercentage > max.boostRatePercentage ? c : max,
  )
  return best.code
}

export const useSetAppliedCoupon = () => {
  const setter = useSetAtom(appliedCouponAtom)
  return useCallback((coupon: string) => setter(coupon), [setter])
}

export const useRemoveAppliedCoupon = () => {
  const setter = useSetAtom(appliedCouponAtom)
  return useCallback(() => setter(null), [setter])
}

// Mark coupon system as initialized after first data load.
// Does NOT set appliedCouponAtom — useEffectiveAppliedCoupon handles auto-selecting the best boost.
export function useInitializeDefaultCoupon() {
  const { isFetched } = useMyAvailableBoosts()
  const [initialized, setInitialized] = useAtom(defaultCouponInitializedAtom)

  useEffect(() => {
    if (!initialized && isFetched) {
      setInitialized(true)
    }
  }, [initialized, isFetched, setInitialized])
}

// Get full info of currently selected coupon
export function useSelectedCouponInfo() {
  const { data: coupons } = useMyAvailableBoosts()
  const effectiveCode = useEffectiveAppliedCoupon()

  return effectiveCode
    ? (coupons?.find(c => c.code === effectiveCode) ?? null)
    : null
}

export function useCouponListReady() {
  const { isFetched } = useMyAvailableBoosts()
  return isFetched
}

// Return type for useAvailableCouponCount
export interface CouponCountInfo {
  count: number
  isLoading: boolean
}

export function useAvailableCouponCount(): CouponCountInfo {
  const { data, isPending } = useMyAvailableBoosts()
  return {
    count: data?.length ?? 0,
    isLoading: isPending,
  }
}
// ---------- Applied Coupon ----------

// ---------- History Coupon ----------
const historyCouponExpandedAtom = atomWithReset<boolean>(false)

export function useHistoryCouponExpanded() {
  return useAtomValue(historyCouponExpandedAtom)
}

export function useToggleHistoryCouponExpanded() {
  const setter = useSetAtom(historyCouponExpandedAtom)
  return useCallback(() => {
    setter(prev => !prev)
  }, [setter])
}
// ---------- History Coupon ----------

// ---------- Reset / Invalidation ----------
export function useResetAllCouponStates() {
  const resetAppliedCoupon = useResetAtom(appliedCouponAtom)
  const resetInitialized = useResetAtom(defaultCouponInitializedAtom)
  const resetBoostQueries = useResetBoostQueries()

  return useCallback(() => {
    resetAppliedCoupon()
    resetInitialized()
    // resetQueries clears stale cached data (preventing auto-selection of a
    // consumed coupon) AND triggers a fresh refetch for active observers.
    resetBoostQueries()
  }, [resetAppliedCoupon, resetInitialized, resetBoostQueries])
}

export function useInvalidateCouponList() {
  return useInvalidateBoosts()
}
// ---------- Reset / Invalidation ----------

// ---------- Auth Handler ----------
// Handle coupon state on user login/logout
export function CouponAuthHandler() {
  const { data: userInfo, loading } = useMyInfo()
  const userId = userInfo?.id

  const prevUserIdRef = useRef<string | undefined>(undefined)

  const resetAppliedCoupon = useResetAtom(appliedCouponAtom)
  const resetInitialized = useResetAtom(defaultCouponInitializedAtom)
  const resetHistoryExpanded = useResetAtom(historyCouponExpandedAtom)
  const removeBoostQueries = useRemoveBoostQueries()

  useEffect(() => {
    if (loading) return

    const prevUserId = prevUserIdRef.current
    const currentUserId = userId

    // User logout or login: reset UI state
    const isUserChange =
      (prevUserId && !currentUserId) || (!prevUserId && currentUserId)
    if (isUserChange) {
      resetAppliedCoupon()
      resetInitialized()
      resetHistoryExpanded()
      // On logout, remove cached query data immediately
      if (!currentUserId) {
        removeBoostQueries()
      }
    }

    prevUserIdRef.current = currentUserId
  }, [
    userId,
    loading,
    resetAppliedCoupon,
    resetInitialized,
    resetHistoryExpanded,
    removeBoostQueries,
  ])

  return null
}
